/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { RequestStatus } from "~/constants";
import { deleteReportAsync, loadReportsMetadataAsync } from "~/features/reportTracker/thunks";
import { ReportTrackerState } from "~/features/reportTracker/types";

const initialState: ReportTrackerState = {
  reports: {
    data: [],
    status: RequestStatus.IDLE,
  },
  isReportTrackerOpen: false,
  shouldRefreshTracker: false,
  sites: {},
};

const reportTrackerSlice = createSlice({
  name: "report",
  initialState,
  reducers: {
    setIsReportTrackerOpen: (state, action: PayloadAction<boolean>) => {
      state.isReportTrackerOpen = action.payload;
    },
    setSitesByName: (state, action: PayloadAction<Record<string, number>>) => {
      state.sites = action.payload;
    },
    setShouldRefreshTracker: (state, action: PayloadAction<boolean>) => {
      state.shouldRefreshTracker = action.payload;
    },
    clearState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadReportsMetadataAsync.pending, (state) => {
        state.reports.status = RequestStatus.PENDING;
      })

      .addCase(loadReportsMetadataAsync.fulfilled, (state, action) => {
        state.reports.status = RequestStatus.SUCCESS;
        state.reports.data = action.payload.sort((a, b) => {
          // We want to display the pending reports at the top, and the reports should be ordered by the timestamp
          const timestampA = Number(a.cacheKey.split(".")[2]);
          const timestampB = Number(b.cacheKey.split(".")[2]);
          return a.status.localeCompare(b.status) * 10 + (timestampA < timestampB ? 1 : -1);
        });
      })

      .addCase(loadReportsMetadataAsync.rejected, (state) => {
        state.reports.status = RequestStatus.ERROR;
      })

      .addCase(deleteReportAsync.fulfilled, (state, action) => {
        state.shouldRefreshTracker = action.payload;
      });
  },
});

export const reportTrackerActions = reportTrackerSlice.actions;

export const reportTrackerReducer = reportTrackerSlice.reducer;
